import { TextField, MenuItem } from '@mui/material';

export interface DropdownOption {
  id: string;
  label: string;
}

interface DropdownFieldProps {
  label: string;
  /** Current selected value — stored as the option's `label` string (matching Angular behaviour). */
  value: unknown;
  options: DropdownOption[];
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  'data-testid'?: string;
}

function extractPrimitive(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    if (obj.label != null && obj.label !== '') return String(obj.label);
    if (obj.value != null && obj.value !== '') return extractPrimitive(obj.value);
    if (obj.amount != null && obj.amount !== '') return extractPrimitive(obj.amount);
    if (obj.input != null && obj.input !== '') return extractPrimitive(obj.input);
  }
  return '';
}

/**
 * Reusable dropdown (select) field.
 * Mirrors Angular's `sin-dropdown` / `<select>` rendering for `dropdown` fieldType.
 * Selected value is stored as the option label string to stay consistent with the Angular form.
 */
export function DropdownField({
  label,
  value,
  options,
  onChange,
  required = false,
  placeholder,
  'data-testid': dataTestId,
}: DropdownFieldProps) {
  const raw = extractPrimitive(value).trim();
  const optionValues = new Set(options.map((opt) => opt.label));
  const safeValue = optionValues.has(raw) ? raw : '';

  return (
    <TextField
      select
      size="small"
      label={label + (required ? ' *' : '')}
      fullWidth
      value={safeValue}
      onChange={(e) => onChange(e.target.value)}
      data-testid={dataTestId}
    >
      <MenuItem value="">
        <em>Choose</em>
      </MenuItem>
      {options.map((opt) => (
        <MenuItem key={opt.id} value={opt.label}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>
  );
}
